/**
 * The inspector crosswalk (owner, 2026-10-04: "checking what the health inspectors need so that the logs are able to accommodate
 * both with least friction"). Each health authority's own inspection form, item by item, in ITS order, with what the inspector
 * looks at, what proof answers it (a log, a certificate, a posting, a record, or only what they see on site), how often, the
 * citation, and which CleanCleaver records speak to it. The forms live in each state's bundle (states/<st>/inspector.ts); the
 * FDA model items (inspectionform.ts FORM_ITEMS) carry the shared meaning, so a state form that prints the FDA items reuses it.
 *
 * Gates (test/inspector.test.ts):
 * | X1 Every item answered | each item maps to records, or says "Observed on site" (observation only) or "Not covered" (a named gap) |
 * | X2 No citation without a source | each form has a source URL, a quote of 15 words or fewer and a lastChecked date; each item a citation |
 * | X3 Their order | the view, the binder and the estimate list items in the form's own order |
 * | X4 Never a pass | the words are "A problem on file", "Records on file", "Nothing on file yet", "Observed on site", "Not covered" |
 * | X5 Least friction | what this adds is derived from records the app already keeps: added taps per day per persona stay under FRICTION_CAP |
 */
import type { Credential, CredentialKind, IsoDate } from "./model.ts";
import type { HealthItem } from "./locationhealth.ts";
import { FORM_ITEMS, type FormFact, type FormRow } from "./inspectionform.ts";
import { getTemplate } from "./templates.ts";
import { departmentFor } from "./localrules.ts";
import { countyIn, stateOrDefault } from "./states/index.ts";
import { perStateTable } from "./states/access.ts";
import { INSPECTOR_UT } from "./states/ut/inspector.ts";
import type { Source, UsState } from "./states/types.ts";

// ── The data model ───────────────────────────────────────────────────────────────────────────────────────────────────

/** What answers an item: a log the staff fill, a record kept once, a certificate, a posted sign, or only what the inspector sees. */
export type ProofKind = "log" | "record" | "certificate" | "posting" | "observation";

/**
 * Which CleanCleaver records speak to an item. Any of fda, tags, cred, picCert or signs = covered by records. `observe` alone =
 * observation only (the inspector looks on site; no record would add anything). `gap` alone = not covered (a named gap).
 */
export interface InspectorEvidence {
  /** FDA Form 3-A items (FORM_ITEMS) whose records and facts speak to this item. */
  fda?: number[];
  /** Extra health item tags (HealthItem.tags), the same matching as FORM_ITEMS. */
  tags?: string[];
  /** A current credential of this kind on the team (People: card photos and expiry dates). */
  cred?: CredentialKind[];
  /** The person in charge today, and whether they hold a current manager certificate: derived, no tap. */
  picCert?: boolean;
  /**
   * A certificate someone on today's shift must hold, only where the place seats at least `minSeats` (Massachusetts choke-saving at
   * 25+, M.G.L. c.94 §305D): derived from the shift and People, no tap. Seats not answered = the channel is skipped.
   */
  onShiftCert?: { kind: CredentialKind; minSeats: number };
  /** Signs (signs.ts ids) marked posted on the Signs page. */
  signs?: string[];
  /** Observation only: what the inspector looks at on site. */
  observe?: string;
  /** Not covered: what is missing, in plain words. */
  gap?: string;
}

export interface InspectorItemDef {
  /** The item's number or code on the form, as printed ("22", "03A", "04L", "1a"). */
  id: string;
  /** The item as the form prints it. */
  text: string;
  /** The form's own section or class for the item (keys of the form's `tiers`). */
  tier: string;
  /** The violation classes this item can be cited as (Florida: High Priority, Intermediate, Basic). */
  classes?: string[];
  /** Where the rule is: the form's item, the code section. */
  cite: string;
  /** What the inspector looks at. Absent = from the FDA items it maps to. */
  looks?: string;
  proof?: ProofKind[];
  /** How often the proof is made ("each delivery", "daily", "monthly", "kept current"). */
  often?: string;
  ev: InspectorEvidence;
  /** With `fdaItems`: the form prints this item right after that FDA item ("1a" after "1" on Texas's EH-06). Absent = at the end. */
  after?: string;
}

/**
 * How the form turns findings into a result, as the authority publishes it. `deduct`: start at `startAt` and take points off per
 * violation; an item's points are a range when the class of a finding is not known in advance (Boston: 2, 7 or 10). `classes`:
 * findings are classed (Florida DBPR) and nothing is graded.
 */
export type FormScoring =
  | { kind: "deduct"; startAt: number; points: Record<string, [number, number]>; source: Source; note?: string }
  | { kind: "classes"; classes: { id: string; label: string; meaning: string }[]; graded: false; source: Source };

export interface InspectorFormDef {
  id: string;
  /** "Food Establishment Inspection Report (105 CMR 590)". */
  name: string;
  /** Who inspects with it, in plain words. */
  issuer: string;
  /** The issuer's short name for a sentence ("DBPR"). Absent = `issuer`. */
  issuerShort?: string;
  /** Where it applies: the whole state, these health authorities (department ids), or authorities whose id starts with a prefix. */
  appliesTo: { state: true } | { departments: string[] } | { departmentPrefix: string };
  source: Source;
  /** A copy we could not read ourselves (a bot check): say so, never guess. */
  confirmByHand?: string;
  /** A plain note on how this form is used here. */
  note?: string;
  tiers: Record<string, string>;
  scoring?: FormScoring;
  /**
   * The form prints the FDA items 1–56 in their order (Massachusetts, Texas 2021): core expands them (formItems), each cited with
   * `cite` ("{n}" = the item number) and with `add` merged into an item's evidence. `items` then follow them.
   */
  fdaItems?: { cite: string; add?: Record<string, InspectorEvidence>; /** Where the form prints an item in its own words. */ text?: Record<string, string> };
  items: InspectorItemDef[];
}

/** A state's forms, most specific first is not required: department forms win over a statewide one (inspectorFormFor). */
export interface InspectorStateDef { forms: readonly InspectorFormDef[] }

const TABLE: Record<UsState, InspectorStateDef> = perStateTable("INSPECTOR", INSPECTOR_UT) as Record<UsState, InspectorStateDef>;

/** The form the inspector at this place uses: the authority's own, else the state's, else none (not mapped). */
export function inspectorFormFor(place: { state?: string | null; county?: string | null }): InspectorFormDef | null {
  const st = stateOrDefault(place.state);
  const c = countyIn(st, place.county);
  return inspectorFormAt(st, c ? departmentFor(st, c)?.id : undefined);
}

/** The form for a state and its health authority (department id; absent = no county picked): the authority's, else the state's. */
export function inspectorFormAt(st: UsState, dep: string | undefined): InspectorFormDef | null {
  const forms = TABLE[st]?.forms ?? [];
  if (!forms.length) return null;
  const byDept = dep ? forms.find((f) => ("departments" in f.appliesTo && f.appliesTo.departments.includes(dep)) || ("departmentPrefix" in f.appliesTo && dep.startsWith(f.appliesTo.departmentPrefix))) : undefined;
  return byDept ?? forms.find((f) => "state" in f.appliesTo) ?? null;
}

/** Every form of a state (tests, the rules dump). */
export const inspectorFormsOf = (state: UsState): readonly InspectorFormDef[] => TABLE[state]?.forms ?? [];

// ── The FDA model items: what each one asks for ─────────────────────────────────────────────────────────────────────

export interface FdaMeta { looks: string; proof: ProofKind[]; often: string; ev?: Omit<InspectorEvidence, "fda"> }

/**
 * What the inspector looks at for each FDA Form 3-A item (Food Code 2022 Annex 7, Guide 3-B, the marking instructions), the proof
 * that answers it and its cadence. Items 2, 7, 14, 27, 34 and 46 have no health-item tags in FORM_ITEMS; their evidence is here.
 */
export const FDA_META: Record<number, FdaMeta> = {
  1: { looks: "A person in charge on site who answers food safety questions and runs the shift", proof: ["record", "observation"], often: "every shift", ev: { picCert: true } },
  2: { looks: "A certified food protection manager's certificate", proof: ["certificate"], often: "kept current", ev: { cred: ["certified_food_safety_manager"], picCert: true } },
  3: { looks: "Staff know to report illness; the reporting agreement or check-ins", proof: ["record", "observation"], often: "each shift" },
  4: { looks: "Sick workers kept off food work or sent home", proof: ["record"], often: "each shift" },
  5: { looks: "A written plan and kit for vomit and diarrhea cleanup", proof: ["record"], often: "kept on file; each event" },
  6: { looks: "No eating, drinking or smoking where food is made; tasting done right", proof: ["observation", "log"], often: "each visit" },
  7: { looks: "No worker with discharge from eyes, nose or mouth handling food", proof: ["observation"], often: "each visit", ev: { observe: "The inspector watches the staff at work." } },
  8: { looks: "Hands washed when and how the code says", proof: ["observation", "record"], often: "each visit" },
  9: { looks: "Gloves or utensils for ready-to-eat food, no bare hands", proof: ["observation", "log"], often: "each visit" },
  10: { looks: "Hand sinks reachable, with soap, towels and warm water", proof: ["log", "observation"], often: "daily" },
  11: { looks: "Invoices or receipts from approved suppliers", proof: ["log", "record"], often: "each delivery" },
  12: { looks: "Cold and hot food at the right temperature when it arrives", proof: ["log"], often: "each delivery" },
  13: { looks: "Food in sound condition, not spoiled or contaminated", proof: ["log", "observation"], often: "each delivery" },
  14: { looks: "Shellstock tags kept 90 days; parasite destruction letters for raw fish", proof: ["record"], often: "each delivery", ev: { gap: "Shellstock tags and parasite destruction letters are not kept here. Only places that serve shellfish or raw fish need them." } },
  15: { looks: "Raw animal food below and apart from ready-to-eat food; food covered", proof: ["log", "observation"], often: "daily" },
  16: { looks: "Food-contact surfaces washed, rinsed and sanitized; sanitizer strength", proof: ["log"], often: "each use; sanitizer checked daily" },
  17: { looks: "Returned or unsafe food thrown out, not served again", proof: ["log"], often: "each event" },
  18: { looks: "Cooking temperatures", proof: ["log"], often: "each batch" },
  19: { looks: "Reheating to 165°F for hot holding", proof: ["log"], often: "each batch" },
  20: { looks: "Cooling time and temperatures", proof: ["log"], often: "each batch" },
  21: { looks: "Hot food at 135°F or above", proof: ["log"], often: "during holding" },
  22: { looks: "Cold food at 41°F or below", proof: ["log"], often: "daily" },
  23: { looks: "Ready-to-eat food dated and used or thrown out in time", proof: ["record"], often: "each container" },
  24: { looks: "Written time-control procedure and marked times", proof: ["record", "log"], often: "each batch" },
  25: { looks: "The consumer advisory on the menu for raw or undercooked food", proof: ["posting", "log"], often: "when the menu changes" },
  26: { looks: "Pasteurized food used and prohibited food not served, where people are highly susceptible", proof: ["log"], often: "when the menu changes" },
  27: { looks: "Only approved additives, used the right way", proof: ["observation"], often: "each visit", ev: { observe: "The inspector reads the labels and the use." } },
  28: { looks: "Chemicals labeled, stored apart and below food, used right", proof: ["observation", "record"], often: "each visit" },
  29: { looks: "The variance, HACCP plan or special process records", proof: ["record"], often: "as the plan says" },
  30: { looks: "Pasteurized eggs where the recipe needs them", proof: ["log"], often: "when the menu changes" },
  31: { looks: "Water and ice from an approved source", proof: ["record", "log"], often: "kept on file" },
  32: { looks: "A variance for any specialized process", proof: ["record"], often: "kept on file" },
  33: { looks: "Cooling methods and enough equipment to hold temperatures", proof: ["log", "observation"], often: "daily" },
  34: { looks: "Plant food cooked to 135°F for hot holding", proof: ["log"], often: "each batch", ev: { tags: ["tpl:temp_cook_hold"] } },
  35: { looks: "Thawing in the fridge, under running water, in the microwave or as part of cooking", proof: ["record", "observation"], often: "each thaw" },
  36: { looks: "Thermometers on hand, accurate and calibrated", proof: ["log"], often: "monthly calibration" },
  37: { looks: "Food labeled with its common name; original containers", proof: ["record", "observation"], often: "each container" },
  38: { looks: "No insects, rodents or animals; pest control records", proof: ["log", "record"], often: "monthly; each sighting" },
  39: { looks: "Food protected from contamination in storage and display", proof: ["log", "observation"], often: "weekly" },
  40: { looks: "Clean clothes, hair restraints, no jewelry on hands", proof: ["log", "observation"], often: "each shift" },
  41: { looks: "Wiping cloths in sanitizer between uses", proof: ["log", "observation"], often: "daily" },
  42: { looks: "Fruits and vegetables washed before use", proof: ["log", "observation"], often: "each batch" },
  43: { looks: "In-use utensils stored the right way", proof: ["log", "observation"], often: "daily" },
  44: { looks: "Clean utensils and linens stored, dried and handled right", proof: ["observation"], often: "each visit" },
  45: { looks: "Cups, lids and single-use items stored and used right", proof: ["log", "observation"], often: "weekly" },
  46: { looks: "Gloves used once and changed between tasks", proof: ["observation", "log"], often: "each visit", ev: { tags: ["tpl:hands_glove"] } },
  47: { looks: "Equipment cleanable, in good repair and used right", proof: ["observation", "record"], often: "each visit" },
  48: { looks: "Dish machine or three-compartment sink set up and used; test strips on hand", proof: ["log", "observation"], often: "daily" },
  49: { looks: "Non-food-contact surfaces clean", proof: ["log", "observation"], often: "weekly" },
  50: { looks: "Hot and cold water with enough pressure", proof: ["log"], often: "daily" },
  51: { looks: "Plumbing in good repair; backflow devices and their tests", proof: ["record"], often: "yearly test" },
  52: { looks: "Sewage and waste water disposed of the right way", proof: ["observation", "record"], often: "each event" },
  53: { looks: "Toilet rooms built right, supplied and clean", proof: ["log"], often: "daily" },
  54: { looks: "Garbage covered, removed and its area kept clean", proof: ["observation", "log"], often: "daily" },
  55: { looks: "Floors, walls and ceilings in good repair and clean", proof: ["observation", "log"], often: "weekly" },
  56: { looks: "Ventilation and lighting enough; hood filters clean; areas used as designed", proof: ["observation", "log"], often: "monthly" },
};

/** The FDA item's title and evidence: its FORM_ITEMS tags and facts plus FDA_META's extras. */
export function fdaEvidence(n: number): InspectorEvidence {
  return { fda: [n], ...(FDA_META[n]?.ev ?? {}) };
}

/** A form that prints the FDA items in their order (Massachusetts, Texas 2021): one item per FDA item, cited to the form. */
export function fdaFormItems(cite: (n: number) => string, tierOf: (n: number) => string = (n) => (n <= 29 ? "risk" : "grp")): InspectorItemDef[] {
  return FORM_ITEMS.map((d) => ({ id: String(d.n), text: d.title, tier: tierOf(d.n), cite: cite(d.n), ev: fdaEvidence(d.n) }));
}

const EXPANDED = new WeakMap<InspectorFormDef, InspectorItemDef[]>();
/** The form's items in its own order (X3): the FDA items when it prints them, then its own. */
export function formItems(form: InspectorFormDef): InspectorItemDef[] {
  const hit = EXPANDED.get(form);
  if (hit) return hit;
  const fda = form.fdaItems ? fdaFormItems((n) => form.fdaItems!.cite.replace("{n}", String(n))).map((it) => {
    const add = form.fdaItems!.add?.[it.id];
    const own = form.fdaItems!.text?.[it.id];
    const t = own ? { ...it, text: own } : it;
    return add ? { ...t, ev: { ...t.ev, ...add, ...(add.signs ? { signs: [...(t.ev.signs ?? []), ...add.signs] } : {}) } } : t;
  }) : [];
  const out = [...fda];
  for (const it of form.items) {
    const at = it.after ? out.findIndex((x) => x.id === it.after) : -1;
    if (at >= 0) out.splice(at + 1, 0, it); else out.push(it);
  }
  EXPANDED.set(form, out);
  return out;
}

/** An item's evidence with the FDA items' extras merged in (picCert, cred, tags), so a state form that maps to item 2 gets its certificate. */
export function evidenceOf(it: Pick<InspectorItemDef, "ev">): InspectorEvidence {
  const out: InspectorEvidence = { ...it.ev };
  for (const n of it.ev.fda ?? []) {
    const x = FDA_META[n]?.ev;
    if (!x) continue;
    if (x.tags) out.tags = [...new Set([...(out.tags ?? []), ...x.tags])];
    if (x.cred) out.cred = [...new Set([...(out.cred ?? []), ...x.cred])];
    if (x.picCert) out.picCert = true;
    // An item that maps only to FDA items with no record gets their words: "observed on site" or the named gap.
    if (x.observe && !out.observe) out.observe = x.observe;
    if (x.gap && !out.gap) out.gap = x.gap;
  }
  return out;
}

/** True when an item has a record channel (X1): records, a certificate, a posting or a derived fact. */
export const hasRecords = (e: InspectorEvidence): boolean => !!(e.fda?.some((n) => { const d = FORM_ITEMS.find((f) => f.n === n); return !!(d?.tags?.length || d?.facts?.length); }) || e.tags?.length || e.cred?.length || e.picCert || e.onShiftCert || e.signs?.length);

/** The looks / proof / often of an item: its own, else gathered from the FDA items it maps to. */
export function itemMeta(it: InspectorItemDef): { looks: string; proof: ProofKind[]; often: string } {
  const metas = (it.ev.fda ?? []).map((n) => FDA_META[n]).filter((m): m is FdaMeta => !!m);
  return {
    looks: (it.looks ?? (metas.map((m) => m.looks).join("; ") || it.ev.observe || it.text)).replace(/\.$/, ""),
    proof: it.proof ?? [...new Set(metas.flatMap((m) => m.proof))].sort(),
    often: it.often ?? (metas[0]?.often ?? "each visit"),
  };
}

// ── The words (never a pass) ─────────────────────────────────────────────────────────────────────────────────────────

export type InspectorStatus = "problem" | "on_file" | "nothing_yet" | "observed" | "not_covered";
export const INSPECTOR_WORD: Record<InspectorStatus, string> = { problem: "A problem on file", on_file: "Records on file", nothing_yet: "Nothing on file yet", observed: "Observed on site", not_covered: "Not covered" };
export const INSPECTOR_TONE: Record<InspectorStatus, "stop" | "go" | "info" | "neutral" | "warn"> = { problem: "stop", on_file: "go", nothing_yet: "info", observed: "neutral", not_covered: "warn" };

// ── The screen view: from today's health items ───────────────────────────────────────────────────────────────────────

export interface InspectorFacts {
  today: IsoDate;
  /** The team's credentials (kind and expiry only). */
  credentials?: readonly Pick<Credential, "kind" | "expiresOn">[];
  /** Today's person in charge: a first name, and whether they hold a current manager certificate. */
  pic?: { name: string; certified: boolean };
  /** Signs marked posted (signs.ts ids) and the day. */
  posted?: readonly { id: string; date: IsoDate }[];
  /** Today's shift: first names and their credentials (kind and expiry), for a certificate someone on shift must hold. */
  onShift?: readonly { name: string; credentials?: readonly Pick<Credential, "kind" | "expiresOn">[] }[];
  /** Location.seats; absent = not answered. */
  seats?: number;
  /** "Sep 30" for a date (the location's labels). */
  day?: (d: IsoDate) => string;
}

export interface InspectorRow {
  id: string;
  text: string;
  tier: string;
  tierLabel: string;
  classes?: string[];
  cite: string;
  looks: string;
  proof: ProofKind[];
  often: string;
  status: InspectorStatus;
  word: string;
  /** "Fridge and freezer log: 3 on file · last 11:05 · Sam · Manager certificate: 1 current, to Mar 2028". */
  line: string;
  /** The FDA items behind it, with their own status (for the estimate). */
  fda: number[];
  /** Health item ids behind it (one finding is counted once in the estimate). */
  itemIds: string[];
  failing?: boolean;
}

export interface InspectorView {
  formId: string;
  name: string;
  issuer: string;
  source: Source;
  confirmByHand?: string;
  note?: string;
  rows: InspectorRow[];
  counts: Record<InspectorStatus, number>;
  /** "Their form: 61 items · 48 with records · 9 observed on site · 4 not covered". */
  line: string;
}

const RANK: Record<InspectorStatus, number> = { problem: 0, on_file: 1, nothing_yet: 2, observed: 3, not_covered: 4 };
const tagMatch = (want: string, tags: readonly string[]) => (want.endsWith(":") ? tags.some((t) => t.startsWith(want)) : tags.includes(want));
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const defaultDay = (d: IsoDate) => `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}, ${d.slice(0, 4)}`;
const SIGN_WORD = (id: string) => id.replace(/^posting-/, "").replace(/-/g, " ").replace(/\b(cfpm|nyc|snhd|nv)\b/g, (w) => w.toUpperCase()).replace(/\bheimlich\b/, "Heimlich");
const CRED_WORD: Record<CredentialKind, string> = { certified_food_safety_manager: "Manager certificate", food_handler_permit: "Food handler cards", food_handler_course: "Food handler course certificates", choking_first_aid: "Choking first aid (trained)", allergen_awareness: "Allergen awareness certificate" };

/** The credential line and its status: current ones on file, an expired one is a problem, none is nothing yet. */
function credPart(kinds: readonly CredentialKind[], f: InspectorFacts): { status: InspectorStatus; line: string }[] {
  const day = f.day ?? defaultDay;
  return kinds.map((k) => {
    const mine = (f.credentials ?? []).filter((c) => c.kind === k);
    const current = mine.filter((c) => !c.expiresOn || c.expiresOn >= f.today);
    const expired = mine.length - current.length;
    if (current.length) {
      const soonest = current.map((c) => c.expiresOn).filter((x): x is IsoDate => !!x).sort()[0];
      return { status: "on_file" as const, line: `${CRED_WORD[k]}: ${current.length} current${soonest ? `, first ends ${day(soonest)}` : ""}${expired ? ` (${expired} expired)` : ""}` };
    }
    if (expired) return { status: "problem" as const, line: `${CRED_WORD[k]}: ${plural(expired, "expired", "expired")}, none current` };
    return { status: "nothing_yet" as const, line: `${CRED_WORD[k]}: none on file yet` };
  });
}

/**
 * Each item of the place's own form with what's on file. `fdaRows` are today's FDA rows (inspectionform.ts formReadiness);
 * `items` the health items behind them (for an item's extra tags). Problems first within the item; the order is the form's (X3).
 */
export function inspectorView(form: InspectorFormDef, fdaRows: readonly FormRow[], items: readonly HealthItem[], f: InspectorFacts): InspectorView {
  const byN = new Map(fdaRows.map((r) => [r.n, r]));
  const day = f.day ?? defaultDay;
  const rows: InspectorRow[] = formItems(form).map((it) => {
    const ev = evidenceOf(it);
    const meta = itemMeta(it);
    const parts: { status: InspectorStatus; line: string; ids?: string[]; failing?: boolean }[] = [];
    for (const n of ev.fda ?? []) {
      const r = byN.get(n);
      if (!r || r.status === "not_tracked") continue;
      parts.push({ status: r.status, line: r.line, ids: r.itemIds, ...(r.failing !== undefined ? { failing: r.failing } : {}) });
    }
    if (ev.tags?.length) {
      const mine = items.filter((x) => ev.tags!.some((t) => tagMatch(t, x.tags)));
      const bad = mine.filter((x) => x.status === "failed" || x.status === "late");
      const kept = mine.filter((x) => !!x.lastCheckedAt);
      if (bad.length) parts.push({ status: "problem", line: bad.map((x) => x.short).slice(0, 2).join(" · "), ids: bad.map((x) => x.id), failing: bad.some((x) => x.status === "failed") });
      else if (kept.length) parts.push({ status: "on_file", line: kept.map((x) => x.name).slice(0, 2).join(" · ") + `: ${kept.length} on file`, ids: kept.map((x) => x.id) });
      else parts.push({ status: "nothing_yet", line: `${INSPECTOR_WORD.nothing_yet}: ${ev.tags.map((t) => getTemplate(t.replace(/^tpl:/, ""))?.title ?? t).join(", ").toLowerCase()}` });
    }
    if (ev.picCert) {
      if (f.pic) parts.push({ status: f.pic.certified ? "on_file" : "nothing_yet", line: f.pic.certified ? `In charge today: ${f.pic.name}, certified manager` : `In charge today: ${f.pic.name} (no manager certificate on file)` });
    }
    if (ev.cred?.length) parts.push(...credPart(ev.cred, f));
    if (ev.onShiftCert && f.seats !== undefined && f.seats >= ev.onShiftCert.minSeats) {
      const k = ev.onShiftCert.kind;
      const valid = (c: Pick<Credential, "kind" | "expiresOn">) => c.kind === k && (!c.expiresOn || c.expiresOn >= f.today);
      const trained = (f.onShift ?? []).filter((p) => (p.credentials ?? []).some(valid)).map((p) => p.name);
      if (trained.length) parts.push({ status: "on_file", line: `On shift today, ${CRED_WORD[k].toLowerCase()}: ${trained.join(", ")}` });
      else if (f.onShift?.length) parts.push({ status: "nothing_yet", line: `No one on today's shift has ${CRED_WORD[k].toLowerCase()} on file` });
      parts.push(...credPart([k], f));
    }
    for (const s of ev.signs ?? []) {
      const p = f.posted?.find((x) => x.id === s);
      parts.push(p ? { status: "on_file", line: `Posted: ${SIGN_WORD(s)}, ${day(p.date)}` } : { status: "nothing_yet", line: `Not marked posted yet: ${SIGN_WORD(s)}` });
    }
    const base = { id: it.id, text: it.text, tier: it.tier, tierLabel: form.tiers[it.tier] ?? it.tier, ...(it.classes ? { classes: it.classes } : {}), cite: it.cite, ...meta, fda: ev.fda ?? [] };
    if (!parts.length) {
      if (ev.observe) return { ...base, status: "observed", word: INSPECTOR_WORD.observed, line: `No record for this. ${ev.observe}`, itemIds: [] };
      if (ev.gap) return { ...base, status: "not_covered", word: INSPECTOR_WORD.not_covered, line: ev.gap, itemIds: [] };
      // Records are possible here but none of the channels apply today (an FDA row we don't track): say so.
      return { ...base, status: "nothing_yet", word: INSPECTOR_WORD.nothing_yet, line: INSPECTOR_WORD.nothing_yet, itemIds: [] };
    }
    const status = parts.reduce<InspectorStatus>((m, p) => (RANK[p.status] < RANK[m] ? p.status : m), "not_covered");
    // "Nothing on file yet: receiving log · Nothing on file yet: discard log" reads as one line: "Nothing on file yet: receiving log; discard log".
    const sorted = [...parts].sort((a, b) => RANK[a.status] - RANK[b.status]);
    // Lines that start alike ("Nothing on file yet: …", "Not marked posted yet: …") are merged; the shift's person in charge is said once.
    const lines = sorted.map((p) => p.line).filter((l) => l && !(f.pic && ev.picCert && l === `In charge today: ${f.pic.name}`));
    const groups = new Map<string, string[]>();
    const shown: string[] = [];
    for (const l of lines) {
      const m = /^(Nothing on file yet|Not marked posted yet): (.+)$/.exec(l);
      if (!m) { shown.push(l); continue; }
      if (!groups.has(m[1]!)) { groups.set(m[1]!, []); shown.push(`\u0000${m[1]}`); }
      groups.get(m[1]!)!.push(m[2]!);
    }
    for (const [k, v] of groups) shown[shown.indexOf(`\u0000${k}`)] = `${k}: ${[...new Set(v)].join("; ")}`;
    const failing = parts.some((p) => p.status === "problem" && p.failing !== false);
    return { ...base, status, word: INSPECTOR_WORD[status], line: [...new Set(shown)].slice(0, 3).join(" · "), itemIds: [...new Set(parts.flatMap((p) => p.ids ?? []))], ...(status === "problem" ? { failing } : {}) };
  });
  const counts = { problem: 0, on_file: 0, nothing_yet: 0, observed: 0, not_covered: 0 } as Record<InspectorStatus, number>;
  for (const r of rows) counts[r.status]++;
  const records = counts.problem + counts.on_file;
  return {
    formId: form.id, name: form.name, issuer: form.issuer, source: form.source, ...(form.confirmByHand ? { confirmByHand: form.confirmByHand } : {}), ...(form.note ? { note: form.note } : {}), rows, counts,
    line: `Their form: ${plural(rows.length, "item")} · ${records} with records · ${counts.observed} observed on site · ${counts.not_covered} not covered`,
  };
}

// ── Coverage: what the crosswalk covers, by design (no records needed) ────────────────────────────────────────────────

export type CoverageKind = "records" | "observation" | "not_covered";
/** X1: an item's coverage by design: records (any record channel), observation only, or not covered. */
export function coverageOf(it: InspectorItemDef): CoverageKind {
  const ev = evidenceOf(it);
  if (hasRecords(ev)) return "records";
  if (ev.observe) return "observation";
  return "not_covered";
}
export function coverageCounts(form: InspectorFormDef): Record<CoverageKind, number> {
  const c = { records: 0, observation: 0, not_covered: 0 };
  for (const it of formItems(form)) c[coverageOf(it)]++;
  return c;
}

// ── Readiness: the form's own published scoring ──────────────────────────────────────────────────────────────────────

export type InspectorEstimate =
  | { kind: "range"; low: number; high: number; startAt: number; counted: { id: string; text: string; min: number; max: number }[]; notSeen: number; total: number; citation: string; note?: string }
  | { kind: "classes"; byClass: { id: string; label: string; items: string[] }[]; openItems: number; citation: string };

/**
 * The published scoring applied to open problems on file (P2: an estimate, never a grade). A finding is counted once, on the
 * heaviest item it maps to. An item with no record channel, or only "nothing yet", counts as unseen, never as clean (P3).
 */
export function inspectorEstimate(form: InspectorFormDef, rows: readonly (Pick<FormRow, "n" | "status"> & { failing?: boolean; itemIds?: string[] })[]): InspectorEstimate | null {
  const sc = form.scoring;
  if (!sc) return null;
  const all = formItems(form);
  const byN = new Map(rows.map((x) => [x.n, x.status === "problem" && x.failing === false ? "late" : x.status]));
  const idsOf = new Map(rows.map((x) => [x.n, x.itemIds?.length ? x.itemIds : [`row:${x.n}`]]));
  const claimed = new Set<string>();
  const openOn = (it: InspectorItemDef) => {
    const ids = (it.ev.fda ?? []).filter((n) => byN.get(n) === "problem").flatMap((n) => idsOf.get(n) ?? []);
    const fresh = ids.filter((id) => !claimed.has(id));
    if (!fresh.length) return false;
    for (const id of ids) claimed.add(id);
    return true;
  };
  if (sc.kind === "deduct") {
    const weight = (it: InspectorItemDef) => sc.points[it.tier] ?? [0, 0];
    const counted: { id: string; text: string; min: number; max: number }[] = [];
    for (const it of [...all].sort((a, b) => weight(b)[1] - weight(a)[1] || all.indexOf(a) - all.indexOf(b))) {
      const [min, max] = weight(it);
      if (!(max > 0) || !openOn(it)) continue;
      counted.push({ id: it.id, text: it.text, min, max });
    }
    counted.sort((a, b) => all.findIndex((x) => x.id === a.id) - all.findIndex((x) => x.id === b.id));
    const notSeen = all.filter((it) => !(it.ev.fda ?? []).some((n) => { const s = byN.get(n); return s === "problem" || s === "on_file" || s === "late"; })).length;
    const lo = counted.reduce((a, c) => a + c.max, 0), hi = counted.reduce((a, c) => a + c.min, 0);
    return { kind: "range", low: sc.startAt - lo, high: sc.startAt - hi, startAt: sc.startAt, counted, notSeen, total: all.length, citation: sc.source.citation, ...(sc.note ? { note: sc.note } : {}) };
  }
  const byClass = sc.classes.map((c) => ({ id: c.id, label: c.label, items: [] as string[] }));
  let openItems = 0;
  for (const it of all) {
    if (!openOn(it)) continue;
    openItems++;
    // Counted under the heaviest class this item can be cited as (the inspector decides which).
    const top = sc.classes.find((c) => it.classes?.includes(c.id));
    if (top) byClass.find((b) => b.id === top.id)!.items.push(it.id);
  }
  return { kind: "classes", byClass, openItems, citation: sc.source.citation };
}

// ── The binder: the inspector's view with records and dates ─────────────────────────────────────────────────────────

export interface InspectorBinderFacts {
  from: IsoDate;
  to: IsoDate;
  entries: readonly { templateId: string; date: IsoDate; evaluation: { ok: boolean } }[];
  checkins?: readonly { date: IsoDate }[];
  credentials: readonly Pick<Credential, "kind" | "expiresOn">[];
  incidents: readonly { type: string; reportedAt: string }[];
  /** Signs and whether each is posted (signs.ts SignNeed). */
  signs?: readonly { id: string; posting?: { status: string; date: IsoDate } }[];
  /** Location.seats (a certificate tied to seating is listed only where it applies). */
  seats?: number;
  day: (d: IsoDate) => string;
}

/** One item's evidence in the binder: records with their dates, else "Observed on site", else "Not covered". */
export function inspectorBinderRows(form: InspectorFormDef, b: InspectorBinderFacts): { id: string; text: string; tier: string; status: InspectorStatus; evidence: string }[] {
  return formItems(form).map((it) => {
    const ev = evidenceOf(it);
    const lines: string[] = [];
    let status: InspectorStatus | null = null;
    const bump = (s: InspectorStatus) => { status = status === null || RANK[s] < RANK[status] ? s : status; };
    const tpls = new Set<string>();
    const reports = new Set<string>();
    let checkins = false;
    for (const n of ev.fda ?? []) {
      const d = FORM_ITEMS.find((x) => x.n === n);
      for (const t of d?.tags ?? []) { if (t.startsWith("tpl:")) tpls.add(t.slice(4)); if (t.startsWith("report:")) reports.add(t.slice(7)); }
      if (d?.facts?.includes("checkins")) checkins = true;
    }
    for (const t of ev.tags ?? []) if (t.startsWith("tpl:")) tpls.add(t.slice(4));
    for (const id of tpls) {
      const mine = b.entries.filter((e) => e.templateId === id && e.date >= b.from && e.date <= b.to);
      if (!mine.length) continue;
      const dates = mine.map((e) => e.date).sort();
      const bad = mine.filter((e) => !e.evaluation.ok).length;
      lines.push(`${getTemplate(id)?.title ?? id}: ${plural(mine.length, "record")}, ${b.day(dates[0]!)}${dates.length > 1 ? ` to ${b.day(dates.at(-1)!)}` : ""}${bad ? ` (${bad} outside the limit)` : ""}`);
      bump(bad ? "problem" : "on_file");
    }
    for (const t of reports) {
      const mine = b.incidents.filter((x) => x.type === t && x.reportedAt.slice(0, 10) >= b.from && x.reportedAt.slice(0, 10) <= b.to);
      if (mine.length) { lines.push(`${plural(mine.length, "report")} (${t.replace(/_/g, " ")})`); bump("problem"); }
    }
    if (checkins && b.checkins?.length) {
      const days = new Set(b.checkins.filter((c) => c.date >= b.from && c.date <= b.to).map((c) => c.date));
      if (days.size) { lines.push(`Health check-ins on ${plural(days.size, "day")}`); bump("on_file"); }
    }
    const seatCert = ev.onShiftCert && b.seats !== undefined && b.seats >= ev.onShiftCert.minSeats ? [ev.onShiftCert.kind] : [];
    for (const k of [...(ev.cred ?? []), ...seatCert]) {
      const p = credPart([k], { today: b.to, credentials: b.credentials, day: b.day })[0]!;
      lines.push(p.line);
      bump(p.status);
    }
    for (const s of ev.signs ?? []) {
      const sign = b.signs?.find((x) => x.id === s);
      if (sign?.posting?.status === "posted") { lines.push(`Posted ${b.day(sign.posting.date)}: ${SIGN_WORD(s)}`); bump("on_file"); }
      else if (sign) { lines.push(`Not marked posted: ${SIGN_WORD(s)}`); bump("nothing_yet"); }
    }
    if (status === null) {
      if (hasRecords(ev)) return { id: it.id, text: it.text, tier: it.tier, status: "nothing_yet" as const, evidence: `Nothing in this period${ev.observe ? `. ${ev.observe}` : ""}` };
      if (ev.observe) return { id: it.id, text: it.text, tier: it.tier, status: "observed" as const, evidence: `Observed on site. ${ev.observe}` };
      return { id: it.id, text: it.text, tier: it.tier, status: "not_covered" as const, evidence: `Not covered. ${ev.gap ?? ""}`.trim() };
    }
    return { id: it.id, text: it.text, tier: it.tier, status: status as InspectorStatus, evidence: lines.join("; ") };
  });
}

// ── Friction: what this crosswalk adds to anyone's day (X5) ──────────────────────────────────────────────────────────

/** Taps a day added for any one persona, at most. The control in the test adds one prompt over it and sees the gate fail. */
export const FRICTION_CAP_TAPS_PER_DAY = 1;

export interface FrictionAdd {
  id: string;
  what: string;
  /** Form items it serves, as "<formId>:<itemId>" (every added prompt cites the item it serves). */
  serves: string[];
  /** Persona ids (scripts/qa/personas) it reaches; "*" = everyone at a business. */
  personas: string[];
  how: "derived" | "one_tap" | "typing";
  tapsPerDay: number;
  /** A one-time entry, never daily: what it is and how often ("once per person, renewed when the certificate is"). */
  oneTime?: string;
}

/**
 * Everything the crosswalk adds. All derived from records the app already keeps: the shift's person in charge with their
 * certificate, the People page's credentials, the Signs page's posted marks. Nothing new to tap.
 */
export const FRICTION_ADDED: readonly FrictionAdd[] = [
  { id: "pic-certified", what: "Who is in charge today and whether they hold a current manager certificate, from the shift and People", serves: ["ma-105cmr590:1", "ma-105cmr590:2", "fl-dbpr:53A", "tx-dshs-eh06:2", "ca-la-foir:2", "ny-nyc:04A"], personas: ["*"], how: "derived", tapsPerDay: 0 },
  { id: "credentials", what: "Manager certificates and food handler cards already on People, as evidence", serves: ["ma-105cmr590:2", "fl-dbpr:53A", "fl-dbpr:53B", "tx-dshs-eh06:2", "ca-la-foir:2", "ny-nyc:04A", "ny-nyc:18-11"], personas: ["*"], how: "derived", tapsPerDay: 0 },
  { id: "choking-trained-on-shift", what: "At 25+ seats in Massachusetts: someone on today's shift holds choking first aid training, from the shift and People", serves: ["ma-105cmr590:60", "ma-boston-isd:60"], personas: ["*"], how: "derived", tapsPerDay: 0 },
  { id: "ma-certificates", what: "Massachusetts's allergen awareness certificate and choking first aid training as certificate types on People (photo, optional expiry)", serves: ["ma-105cmr590:60", "ma-boston-isd:60"], personas: ["owner", "restaurant-owner", "bar-manager"], how: "typing", tapsPerDay: 0, oneTime: "once per person, again when the certificate is renewed" },
  { id: "ma-seats", what: "How many seats (Settings), asked only where a rule depends on it (Massachusetts choke-saving at 25)", serves: ["ma-105cmr590:60", "ma-boston-isd:60"], personas: ["owner", "restaurant-owner"], how: "typing", tapsPerDay: 0, oneTime: "once per place" },
  { id: "signs-posted", what: "Signs already marked posted on the Signs page, as evidence (one tap when a sign goes up, already in the app)", serves: ["ma-105cmr590:60", "fl-dbpr:31B", "fl-dbpr:50", "ca-la-foir:47", "ny-nyc:20-04"], personas: ["*"], how: "derived", tapsPerDay: 0 },
];

/** Added taps a day per persona (X5). */
export function frictionByPersona(personas: readonly string[], adds: readonly FrictionAdd[] = FRICTION_ADDED): Record<string, number> {
  return Object.fromEntries(personas.map((p) => [p, adds.filter((a) => a.personas.includes("*") || a.personas.includes(p)).reduce((s, a) => s + a.tapsPerDay, 0)]));
}

/** The person in charge today, as the view needs them (locationhealth.ts). */
export function picFact(pic: { name: string; credentials?: readonly Pick<Credential, "kind" | "expiresOn">[] } | undefined, today: IsoDate): InspectorFacts["pic"] {
  if (!pic) return undefined;
  const certified = (pic.credentials ?? []).some((c) => c.kind === "certified_food_safety_manager" && (!c.expiresOn || c.expiresOn >= today));
  return { name: pic.name, certified };
}


/** Proof kinds in plain words for the binder. */
const PROOF_WORD: Record<ProofKind, string> = { log: "log", record: "record", certificate: "certificate", posting: "posted sign", observation: "seen on site" };

/**
 * The binder's "Inspector's view" (binder.ts): the authority's own form in its order (X3), each item with the records in this
 * binder that answer it and their dates, or "Observed on site", or "Not covered". A map for the inspector, never a pass (X4).
 */
export function inspectorSection(form: InspectorFormDef, b: InspectorBinderFacts): import("./binder.ts").BinderSection {
  const rows = inspectorBinderRows(form, b);
  const items = formItems(form);
  const n = { records: rows.filter((r) => r.status === "on_file" || r.status === "problem").length, observed: rows.filter((r) => r.status === "observed").length, gaps: rows.filter((r) => r.status === "not_covered").length };
  return {
    id: "inspector-view",
    heading: `Inspector's view: ${form.name}`,
    intro: [
      `The inspector's own form (${form.issuer}), item by item in its order: the records in this binder that answer each one (with their dates), what the inspector only sees on site, and what this binder doesn't cover. A map for the inspection, never a pass.`,
      `${rows.length} items: ${n.records} with records in this period, ${n.observed} observed on site, ${n.gaps} not covered.`,
      `Source: ${form.source.citation} (${form.source.url}), checked ${form.source.lastChecked}.`,
      form.confirmByHand ?? "",
    ].filter(Boolean).join(" "),
    table: {
      columns: ["Item", "On their form", "Proof", "In this binder"], widths: [0.5, 2.5, 1.0, 3.2],
      rows: rows.map((r, i) => ({
        cells: [r.id, r.text, itemMeta(items[i]!).proof.map((p) => PROOF_WORD[p]).join(", "), r.evidence],
        ...(r.status === "problem" ? { tone: "bad" as const } : r.status === "not_covered" ? { tone: "warn" as const } : {}),
      })),
    },
  };
}

/** Words in a quote (X2: 15 or fewer). */
export const quoteWords = (q: string): number => q.trim().split(/\s+/).filter(Boolean).length;
const sourceProblems = (s: Source | undefined, where: string): string[] => {
  if (!s) return [`${where}: no source`];
  const out: string[] = [];
  if (!s.citation?.trim()) out.push(`${where}: no citation`);
  if (!/^https:\/\//.test(s.url ?? "")) out.push(`${where}: no https URL`);
  if (!s.quote?.trim()) out.push(`${where}: no quote`);
  else if (quoteWords(s.quote) > 15) out.push(`${where}: quote longer than 15 words`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s.lastChecked ?? "")) out.push(`${where}: no lastChecked date`);
  return out;
};

/**
 * What's wrong with a form's data (X1, X2): a source without a URL, quote or date; an item without a citation, with an unknown
 * tier or class, or with no answer at all (neither records, nor "observed on site", nor a named gap). Empty = sound.
 */
export function inspectorFormProblems(form: InspectorFormDef): string[] {
  const out = [...sourceProblems(form.source, form.id), ...(form.scoring ? sourceProblems(form.scoring.source, `${form.id} scoring`) : [])];
  const items = formItems(form);
  const ids = new Set<string>();
  const classIds = form.scoring?.kind === "classes" ? new Set(form.scoring.classes.map((c) => c.id)) : null;
  for (const it of items) {
    const at = `${form.id}:${it.id}`;
    if (ids.has(it.id)) out.push(`${at}: listed twice`);
    ids.add(it.id);
    if (!it.text.trim()) out.push(`${at}: no text`);
    if (!it.cite.trim()) out.push(`${at}: no citation`);
    if (!(it.tier in form.tiers)) out.push(`${at}: tier "${it.tier}" not on the form`);
    if (form.scoring?.kind === "deduct" && !(it.tier in form.scoring.points)) out.push(`${at}: no points for tier "${it.tier}"`);
    for (const c of it.classes ?? []) if (classIds && !classIds.has(c) && c !== "reporting") out.push(`${at}: class "${c}" not on the form`);
    const ev = evidenceOf(it);
    for (const n of ev.fda ?? []) if (!FORM_ITEMS.some((f) => f.n === n)) out.push(`${at}: FDA item ${n} doesn't exist`);
    if (!hasRecords(ev) && !ev.observe?.trim() && !ev.gap?.trim()) out.push(`${at}: no evidence, no "observed on site", no named gap`);
  }
  return out;
}
